<template>
  <div class="manual-check-list" :class="{'list-light': $store.state.bgLight}">
    <a-alert v-if="$route.query.maintenanceTaskId" :message="'当前运维任务站点 #'+$route.query.siteId" type="info" show-icon class="maintenance-notice" />
    <section class="filter-panel" aria-label="查询条件">
      <div class="filter-toolbar">
        <div class="quick-filters">
          <div class="filter-field date-filter">
            <label>采样时间</label>
            <a-range-picker v-model="dates.samplingDate" format="YYYY-MM-DD" :placeholder="['开始日期', '结束日期']" @change="onDateSamplingDate" />
          </div>
          <div class="filter-field person-filter">
            <label>采样人员</label>
            <a-tree-select v-model="queryParam.samplingUser" v-bind="userSelectProps" placeholder="全部人员" />
          </div>
          <div v-if="showSamplingPoint" class="filter-field point-filter">
            <label>采样点</label>
            <a-select v-model="queryParam.samplingPoint" allowClear placeholder="全部采样点">
              <a-select-option :value="SAMPLING_POINT.INLET">进水口</a-select-option>
              <a-select-option :value="SAMPLING_POINT.OUTLET">出水口</a-select-option>
            </a-select>
          </div>
        </div>
        <div class="filter-actions">
          <a-button type="primary" ghost icon="search" @click="searchQuery">查询</a-button>
          <a-button @click="resetFilters">重置</a-button>
          <button type="button" class="expand-filter" :aria-expanded="advancedOpen" @click="advancedOpen = !advancedOpen">
            {{advancedOpen ? '收起筛选' : '更多筛选'}}<span v-if="advancedFilterCount" class="filter-count">{{advancedFilterCount}}</span><a-icon :type="advancedOpen ? 'up' : 'down'" />
          </button>
          <a-button type="primary" icon="plus" :disabled="addDisabled" :title="addDisabled ? '请先选择一个站点' : '新增人工检测记录'" @click="handleAdd">新增记录</a-button>
        </div>
      </div>
      <div v-show="advancedOpen" class="advanced-filters">
        <div class="advanced-group">
          <h3>到样条件</h3>
          <div class="advanced-fields">
            <div class="filter-field"><label>到样时间</label><a-range-picker v-model="dates.arrivalDate" format="YYYY-MM-DD" :placeholder="['开始日期', '结束日期']" @change="onDateArrivalDate" /></div>
            <div class="filter-field"><label>到样人员</label><a-tree-select v-model="queryParam.arrivalUser" v-bind="userSelectProps" placeholder="全部人员" /></div>
          </div>
        </div>
        <div class="advanced-group testing-filters">
          <h3>检测条件</h3>
          <div class="advanced-fields">
            <div class="filter-field"><label>检测时间</label><a-range-picker v-model="dates.checkDate" format="YYYY-MM-DD" :placeholder="['开始日期', '结束日期']" @change="onDateCheckDate" /></div>
            <div class="filter-field"><label>检测方式</label><a-select v-model="queryParam.checkType" allowClear placeholder="全部方式"><a-select-option :value="CHECK_TYPE.SELF">自测</a-select-option><a-select-option :value="CHECK_TYPE.THIRD_PARTY">第三方实验室</a-select-option></a-select></div>
            <div class="filter-field"><label>实验室</label><a-select v-model="queryParam.companyId" allowClear showSearch optionFilterProp="children" placeholder="全部实验室"><a-select-option v-for="item in companyList" :key="item.id" :value="item.id">{{item.name}}</a-select-option></a-select></div>
          </div>
        </div>
      </div>
    </section>

    <a-alert v-if="userError" message="人员加载失败" type="warning" show-icon class="maintenance-notice"><a-button slot="description" size="small" :loading="userLoading" @click="loadUsers">重新加载人员</a-button></a-alert>
    <div class="list-toolbar">
      <div class="status-tabs" role="radiogroup" aria-label="流程状态">
        <button v-for="item in flowOptions" :key="item.value" type="button" role="radio" :aria-checked="(queryParam.flowStatus || '') === item.value" :class="['status-tab',item.tone,{active:(queryParam.flowStatus || '') === item.value}]" @click="selectFlow(item.value)">
          <span v-if="item.value" class="status-dot"></span>{{item.label}}
        </button>
      </div>
      <span class="record-total">{{loading ? '正在读取记录…' : '共 '+ipagination.total+' 条记录'}}</span>
    </div>
    <div v-if="selectedRowKeys.length" class="selection-bar">
      <span>已选 <strong>{{selectedRowKeys.length}}</strong> 条记录</span>
      <a @click="onClearSelected">取消选择</a>
      <a-button size="small" icon="delete" :loading="deleting" @click="batchDel">删除所选</a-button>
    </div>

    <a-table class="records-table" ref="table" size="middle" rowKey="id" :columns="columns" :dataSource="dataSource" :pagination="ipagination" :loading="loading" :scroll="{x: tableWidth}" :locale="{emptyText:'暂无符合条件的记录'}" :rowSelection="{selectedRowKeys: selectedRowKeys, onChange: onSelectChange, columnWidth:40}" @change="handleTableChange">
      <template slot="site" slot-scope="text"><div class="site-cell" :title="text">{{text || '—'}}</div></template>
      <template slot="sampling" slot-scope="text, record">
        <div class="cell-date">{{record.samplingDate || '—'}}</div>
        <div class="cell-meta"><span>{{record.samplingUser_userName || '未记录人员'}}</span><span v-if="record.samplingPoint === SAMPLING_POINT.INLET || record.samplingPoint === SAMPLING_POINT.OUTLET" :class="['point-tag',record.samplingPoint.toLowerCase()]">{{record.samplingPoint === SAMPLING_POINT.INLET ? '进水口' : '出水口'}}</span></div>
      </template>
      <template slot="arrival" slot-scope="text, record">
        <template v-if="record.arrivalDate"><div class="cell-date">{{record.arrivalDate}}</div><div class="cell-meta">{{record.arrivalUser_userName || '未记录人员'}}</div></template>
        <span v-else class="cell-pending">{{record.flowVersion === FLOW_VERSION.LEGACY ? '历史记录未填写' : '尚未到样'}}</span>
      </template>
      <template slot="testing" slot-scope="text, record">
        <template v-if="record.flowStatus === FLOW_STATUS.COMPLETED">
          <div class="cell-date">{{record.checkDate || '未记录时间'}}</div>
          <div class="cell-meta"><span v-if="record.checkUser_userName">{{record.checkUser_userName}}<span class="meta-separator">·</span></span>{{testingMethod(record)}}</div>
          <div v-if="record.companyName && record.checkType === CHECK_TYPE.THIRD_PARTY" class="cell-detail" :title="record.companyName">{{record.companyName}}</div>
          <div v-if="record.standardName" class="cell-detail" :title="'环境标准：'+record.standardName">标准：{{record.standardName}}</div>
        </template>
        <span v-else class="cell-pending">{{record.flowStatus === FLOW_STATUS.WAIT_TEST ? '待填写检测结果' : '到样后填写'}}</span>
      </template>
      <template slot="flow" slot-scope="text, record">
        <span :class="['flow-tag',flowTone(record.flowStatus)]"><span class="status-dot"></span>{{flowName(record.flowStatus)}}</span>
        <div v-if="record.flowVersion === FLOW_VERSION.LEGACY" class="cell-meta">历史完成记录</div>
        <div v-else class="flow-progress" :aria-label="flowName(record.flowStatus)"><span class="done">采样</span><a-icon type="right" /><span :class="{done:record.flowStatus !== FLOW_STATUS.WAIT_ARRIVAL}">到样</span><a-icon type="right" /><span :class="{done:record.flowStatus === FLOW_STATUS.COMPLETED}">检测</span></div>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="row-actions">
          <a-button v-if="record.canEdit && record.flowStatus !== FLOW_STATUS.COMPLETED" size="small" type="primary" @click="handleEdit(record)">{{record.flowStatus === FLOW_STATUS.WAIT_ARRIVAL ? '填写到样' : '填写检测'}}</a-button>
          <button type="button" class="action-link" @click="handleDetail(record)">查看</button>
          <button v-if="record.canEdit && record.flowStatus === FLOW_STATUS.COMPLETED" type="button" class="action-link" @click="handleEdit(record)">修正</button>
          <button type="button" class="action-link" @click="handleExportWord(record)">导出</button>
          <a-popconfirm v-if="record.canDelete" :disabled="deleting" title="确定删除这条人工检测记录？" placement="left" @confirm="handleDelete(record.id)"><button type="button" class="action-link delete-action" :disabled="deleting">删除</button></a-popconfirm>
        </div>
      </template>
    </a-table>
    <manual-check-drawer ref="modalForm" :pagePath="pagePath" @ok="modalFormOk" />
  </div>
</template>

<script>
import ManualCheckDrawer from './modules/ManualCheckDrawer'
import { JeecgListMixin } from '@/mixins/JeecgListMixin'
import { GetSiteIdMixin } from '@/mixins/GetSiteIdMixin'
import { queryUserTreeList } from '@/api/api'
import { getAction, deleteAction, downloadFile } from '@/api/manage'

import * as manualCheckConfig from '@/utils/manualCheck'
const { FLOW_STATUS, SAMPLING_POINT, CHECK_TYPE, FLOW_VERSION, hasSamplingPoints, FLOW_OPTIONS, FLOW_LABELS, CHECK_TYPE_LABELS, FLOW_TONES } = manualCheckConfig

export default {
  name: 'ManualCheckList',
  mixins: [JeecgListMixin, GetSiteIdMixin],
  components: { ManualCheckDrawer },
  /** 初始化页面录入与异步请求状态。 */
  data() {
    return {
      FLOW_STATUS, SAMPLING_POINT, CHECK_TYPE, FLOW_VERSION,
      pagePath: '/online/manual/check',
      pageTitle: '人工检测',
      addDisabled: true,
      advancedOpen: false,
      deleting: false,
      listRequest: 0,
      maintenanceSiteId: this.$route.query.siteId || null,
      openedMaintenanceRecord: null,
      userTree: [],
      userLoading: false,
      userError: '',
      companyList: [],
      queryParam: {status:'1',scope:null,scopeId:null,siteId:undefined},
      dates: {samplingDate: [], arrivalDate: [], checkDate: []},
      flowOptions: [{value:'',label:'全部记录',tone:'all'}].concat(FLOW_OPTIONS),
      isorter: {column: 'sampling_date', order: 'desc'},
      siteColumn: {dataIndex:'siteName',colName:'site_id',title:'站点',width:180,scopedSlots:{customRender:'site'}},
      columns: [
        {title:'序号',key:'rowIndex',width:40,align:'center',customRender:(text,record,index)=>index+1},
        {title:'采样',dataIndex:'samplingDate',colName:'sampling_date',width:174,sorter:true,scopedSlots:{customRender:'sampling'}},
        {title:'到样',dataIndex:'arrivalDate',colName:'arrival_date',width:174,scopedSlots:{customRender:'arrival'}},
        {title:'检测',dataIndex:'checkDate',colName:'check_date',width:202,sorter:true,scopedSlots:{customRender:'testing'}},
        {title:'流程状态',dataIndex:'flowStatus',width:122,scopedSlots:{customRender:'flow'}},
        {title:'操作',dataIndex:'action',width:202,fixed:'right',scopedSlots:{customRender:'action'}},
      ],
    }
  },
  /** 加载人员和实验室选择项。 */
  created() {
    this.queryParamSelect()
  },
  /** 读取运维任务跳转上下文。 */
  mounted() {
    this.applyMaintenanceContext()
  },
  watch: {
    /** 响应运维任务的路由跳转。 */
    '$route.query': function() {
      this.applyMaintenanceContext()
    },
    /** 随站点切换更新查询范围。 */
    currentSiteId() {
      const ids = this.getTreeCurrentId()
      if (this.maintenanceSiteId && String(ids[1]) !== String(this.maintenanceSiteId)) this.maintenanceSiteId = null
      this.$set(this.queryParam, 'scopeId', ids[1])
      this.$set(this.queryParam, 'scope', ids[0])
      this.$set(this.queryParam, 'siteId', ids[0] === 1 ? ids[1] : undefined)
      this.addDisabled = ids[0] !== 1
      this.onClearSelected()
      if (ids[0] === 1) this.spliceColumn(this.siteColumn)
      else this.spliceColumn(this.siteColumn, 1)
      if (ids[0] === 1 && this.currentSiteType && !hasSamplingPoints(this.currentSiteType)) this.$delete(this.queryParam, 'samplingPoint')
      this.delayLoadData()
    },
    /** 不区分进出水的系统清除采样点筛选。 */
    currentSiteType(value) {
      if (String(this.queryParam.scope) === '1' && value && !hasSamplingPoints(value)) this.$delete(this.queryParam, 'samplingPoint')
    },
  },
  computed: {
    /** 沿用原人员树筛选，限制下拉高度并提示搜索方式。 */
    userSelectProps() {
      return {
        treeData:this.userTree, allowClear:true, showSearch:true, treeNodeFilterProp:'title',
        searchPlaceholder:'输入姓名或部门搜索', dropdownStyle:{maxHeight:'280px',overflow:'auto'},
        notFoundContent:this.userLoading ? '正在加载人员…' : this.userError ? '人员加载失败，请重试' : '未找到匹配人员'
      }
    },
    /** 支持进出水的系统或跨站点查询时展示采样点筛选。 */
    showSamplingPoint() {
      return String(this.queryParam.scope) !== '1' || hasSamplingPoints(this.currentSiteType) || this.dataSource.some(record => record.samplingPointSelectable === true || record.sewage === true || record.samplingPoint === SAMPLING_POINT.INLET || record.samplingPoint === SAMPLING_POINT.OUTLET)
    },
    /** 按实际列宽计算表格滚动宽度。 */
    tableWidth() {
      return this.columns.reduce((total, column) => total + column.width, 0)
    },
    /** 统计已使用的高级筛选条件。 */
    advancedFilterCount() {
      return ['arrivalDateStart','arrivalUser','checkDateStart','checkType','companyId'].filter(key => this.queryParam[key] != null && this.queryParam[key] !== '').length
    },
  },
  methods: {
    /** 读取列表并忽略旧请求，修正删除后的无效页码。 */
    async loadData(arg) {
      if (arg === 1) this.ipagination.current = 1
      const request = ++this.listRequest
      this.loading = true
      try {
        const response = await getAction(this.pagePath, this.getQueryParams())
        if (request !== this.listRequest) return false
        if (!response.success) {
          this.$message.warning(response.message || '读取人工检测记录失败')
          return false
        }
        const result = response.result
        const lastPage = Math.max(1, Math.ceil(result.total / this.ipagination.pageSize))
        if (this.ipagination.current > lastPage) {
          this.ipagination.current = lastPage
          return await this.loadData()
        }
        this.dataSource = result.records || []
        this.ipagination.total = result.total
        this.loadNums++
        return true
      } catch (error) {
        if (request === this.listRequest) this.$message.error(error.message || '列表刷新失败，请重试查询')
        return false
      } finally {
        if (request === this.listRequest) this.loading = false
      }
    },
    /** 删除指定记录并刷新列表。 */
    handleDelete(id) {
      return this.deleteRecords([id], false)
    },
    /** 确认批量删除所选记录。 */
    batchDel() {
      if (this.deleting) return
      if (!this.selectedRowKeys.length) {
        this.$message.warning('请选择要删除的人工检测记录')
        return
      }
      const ids = this.selectedRowKeys.slice()
      this.$confirm({
        title:'确认删除',
        content:'确定删除所选的 '+ids.length+' 条人工检测记录？',
        onOk:() => this.deleteRecords(ids, true),
      })
    },
    /** 删除后立即更新列表，再按现有条件重新查询。 */
    async deleteRecords(ids, batch) {
      if (this.deleting) return false
      this.deleting = true
      try {
        const response = await deleteAction(batch ? this.pagePath+'/deleteBatch' : this.pagePath, batch ? {ids:ids.join(',')} : {id:ids[0]})
        if (!response.success) {
          this.$message.warning(response.message || '删除失败')
          return false
        }
        // 先更新当前列表并使旧查询失效，避免已删除的记录被过期响应覆盖回来。
        this.listRequest++
        const deleted = new Set(ids.map(String))
        const remaining = this.dataSource.filter(record => !deleted.has(String(record.id)))
        this.ipagination.total = Math.max(0, this.ipagination.total - (this.dataSource.length - remaining.length))
        this.dataSource = remaining
        this.ipagination.current = Math.max(1, Math.min(this.ipagination.current, Math.ceil(this.ipagination.total / this.ipagination.pageSize)))
        this.onClearSelected()
        const refresh = this.loadData()
        this.$message.success(response.message || '删除成功')
        await refresh
        return true
      } catch (error) {
        this.$message.error(error.message || '删除失败，请重试')
        return false
      } finally {
        this.deleting = false
      }
    },
    /** 切换流程状态并回到查询第一页。 */
    selectFlow(value) {
      if ((this.queryParam.flowStatus || '') === value) return
      this.$set(this.queryParam, 'flowStatus', value || undefined)
      this.onClearSelected()
      this.searchQuery()
    },
    /** 清除筛选并恢复当前站点查询。 */
    resetFilters() {
      this.onClearSelected()
      this.searchReset({status:'1',scope:null,scopeId:null,siteId:undefined})
    },
    /** 展示统一流程状态名称。 */
    flowName(value) {
      return FLOW_LABELS[value] || '未识别状态'
    },
    /** 按流程状态选择标签颜色。 */
    flowTone(value) {
      return FLOW_TONES[value] || 'all'
    },
    /** 兼容历史检测方式名称。 */
    testingMethod(record) {
      return CHECK_TYPE_LABELS[record.checkType] || record.checkType_dictText || '未记录方式'
    },
    /** 处理运维任务指定站点和记录的跳转。 */
    applyMaintenanceContext() {
      const query = this.$route.query
      const continuing = query.continueRecord === true || query.continueRecord === 'true'
      const recordKey = query.businessId ? String(query.businessId) + ':' + continuing : null
      if (!recordKey) this.openedMaintenanceRecord = null
      if (!query.siteId && !query.businessId && !query.maintenanceTaskId) return
      if (query.siteId) {
        this.maintenanceSiteId = query.siteId
        this.currentSiteId = String(query.siteId)
        this.queryParam.scopeId = query.siteId
        this.queryParam.siteId = query.siteId
        this.queryParam.scope = 1
        this.addDisabled = false
        this.spliceColumn(this.siteColumn)
        this.loadData(1)
        sessionStorage.setItem('siteId', String(query.siteId))
        if (typeof this.resetSetItem === 'function') this.resetSetItem('siteId', String(query.siteId))
      }
      if (recordKey && this.openedMaintenanceRecord !== recordKey) {
        this.openedMaintenanceRecord = recordKey
        getAction(this.pagePath+'/'+query.businessId).then((res) => {
          if (this.openedMaintenanceRecord !== recordKey || String(this.$route.query.businessId) !== String(query.businessId)) return
          if (res.success && res.result) {
            this.$refs.modalForm.disableSubmit = !continuing
            this.$refs.modalForm.title = (continuing ? '继续填写：' : '查看：') + this.pageTitle
            this.$refs.modalForm.edit(res.result)
          } else this.$message.warning(res.message || '检测记录不存在或无权查看')
        }).catch(() => this.$message.error('读取完成记录失败'))
      }
    },
    /** 按记录 ID 请求服务端生成的检测报告。 */
    handleExportWord(record) {
      downloadFile(this.exportFileName(record), this.pagePath + '/exportWord', {id:record.id})
    },
    /** 生成包含站点和采样时间的简洁文件名。 */
    exportFileName(record) {
      let site = String(record.siteName || '').replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, '_').trim().replace(/[ .]+$/, '')
      if(!site) site = record.siteId == null ? '未知站点' : '站点' + record.siteId
      site = site.slice(0, 50)
      let sampling = '采样时间未填写'
      if(record.samplingDate) {
        const date = record.samplingDate.slice(0, 10).replace(/-/g, '')
        const time = record.samplingDate.slice(11, 16).replace(':', '')
        const seconds = record.samplingDate.slice(17, 19)
        sampling = date + time + (seconds && seconds !== '00' ? seconds : '')
      }
      const point = record.samplingPoint === SAMPLING_POINT.INLET ? '_进水' : record.samplingPoint === SAMPLING_POINT.OUTLET ? '_出水' : ''
      return '人工检测（' + site + '_' + sampling + point + '）.docx'
    },
    /** 加载筛选所需人员和实验室选项。 */
    queryParamSelect() {
      this.loadUsers()
      getAction('/ea/company/info/queryCompanyName').then((res) => {
        if (res.success) this.companyList = res.result || []
      })
    },
    /** 人员查询沿用原接口，加载失败时允许重试。 */
    async loadUsers() {
      if(this.userLoading) return
      this.userLoading = true
      this.userError = ''
      try {
        const response = await queryUserTreeList()
        if(!response.success) throw new Error(response.message)
        this.userTree = response.result || []
      } catch(error) { this.userError = error.message || '人员加载失败' }
      finally { this.userLoading = false }
    },
    /** 打开新增记录并保留运维任务关联。 */
    handleAdd() {
      const form = this.$refs.modalForm
      form.disableSubmit = false
      form.add(this.queryParam.siteId)
      if (this.$route.query.maintenanceTaskId) form.model.maintenanceTaskId = this.$route.query.maintenanceTaskId
      form.title = '新增：' + this.pageTitle
      form.showTab = false
      form.showContact = false
    },
    /** 保存成功后清除选择并刷新列表。 */
    modalFormOk() {
      this.onClearSelected()
      this.loadData()
    },
    /** 独立保存到样时间区间。 */
    onDateArrivalDate(value, dates) {
      this.$set(this.queryParam, 'arrivalDateStart', dates[0])
      this.$set(this.queryParam, 'arrivalDateEnd', dates[1])
    },
    /** 独立保存检测时间区间。 */
    onDateCheckDate(value, dates) {
      this.$set(this.queryParam, 'checkDateStart', dates[0])
      this.$set(this.queryParam, 'checkDateEnd', dates[1])
    },
    /** 独立保存采样时间区间。 */
    onDateSamplingDate(value, dates) {
      this.$set(this.queryParam, 'samplingDateStart', dates[0])
      this.$set(this.queryParam, 'samplingDateEnd', dates[1])
    },
  },
}
</script>

<style scoped>
.manual-check-list { --panel:#12325a; --panel-border:#35577e; --text:#e8f1fc; --muted:#a9bfd8; --active:#244d7e; }
.manual-check-list.list-light { --panel:#fff; --panel-border:#dce4ee; --text:#24364c; --muted:#687c93; --active:#e8f2ff; }
.maintenance-notice { margin-bottom:12px; }
.filter-panel { padding:16px; border:1px solid var(--panel-border); border-radius:6px; background:var(--panel); }
.filter-toolbar, .quick-filters, .filter-actions { display:flex; align-items:flex-end; gap:12px; }
.filter-toolbar { justify-content:space-between; flex-wrap:wrap; }
.quick-filters { flex-wrap:wrap; }
.filter-field { min-width:0; }
.filter-field label { display:block; margin-bottom:7px; font-size:12px; color:var(--muted); }
.date-filter { width:276px; }
.person-filter { width:180px; }
.point-filter { width:150px; }
.filter-field /deep/ .ant-select, .filter-field /deep/ .ant-calendar-picker { width:100%; }
.filter-actions { gap:8px; flex-wrap:wrap; }
.expand-filter { height:32px; border:0; background:transparent; color:var(--text); cursor:pointer; padding:0 4px; }
.expand-filter /deep/ .anticon { margin-left:6px; font-size:11px; }
.filter-count { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; margin-left:5px; padding:0 4px; border-radius:9px; background:var(--active); color:var(--text); font-size:11px; }
.advanced-filters { display:grid; grid-template-columns:2fr 3fr; gap:24px; margin-top:16px; padding-top:14px; border-top:1px solid var(--panel-border); }
.advanced-group h3 { font-size:12px; font-weight:600; color:var(--text); margin:0 0 10px; }
.advanced-fields { display:grid; grid-template-columns:minmax(230px,1.4fr) minmax(140px,1fr); gap:12px; }
.testing-filters .advanced-fields { grid-template-columns:minmax(230px,1.4fr) minmax(130px,.8fr) minmax(150px,1fr); }
.list-toolbar { display:flex; align-items:center; justify-content:space-between; gap:16px; margin:18px 0 12px; }
.status-tabs { display:flex; flex-wrap:wrap; gap:4px; }
.status-tab { display:flex; align-items:center; gap:7px; padding:8px 16px; border:1px solid transparent; border-radius:5px; background:transparent; color:var(--muted); cursor:pointer; font-size:14px; }
.status-tab.active { background:var(--active); border-color:#528dcc; color:var(--text); font-weight:600; }
.status-tab:hover { background:var(--active); color:var(--text); }
.status-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:currentColor; flex-shrink:0; }
.status-tab.arrival .status-dot { background:#f5b84b; }
.status-tab.testing .status-dot { background:#61a7ff; }
.status-tab.completed .status-dot { background:#55c69c; }
.record-total { font-size:12px; white-space:nowrap; color:var(--muted); }
.selection-bar { display:flex; align-items:center; gap:16px; padding:8px 12px; margin-bottom:10px; color:var(--text); background:var(--panel); border:1px solid var(--panel-border); border-radius:5px; }
.selection-bar a { color:#79b9ff; }
.records-table /deep/ .ant-table { background:#fff; border-radius:6px; overflow:hidden; }
.records-table /deep/ .ant-table-thead { background:#f2f5f9; }
.records-table /deep/ .ant-table-selection-column { min-width:40px; width:40px; }
.records-table /deep/ .ant-table-thead > tr > th { background:#f2f5f9; color:#4b5e73; font-weight:600; white-space:nowrap; border-bottom:1px solid #dfe6ef; }
.records-table /deep/ .ant-table-tbody > tr > td { padding:14px 16px; border-bottom:1px solid #edf0f5; vertical-align:middle; }
.records-table /deep/ .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td { background:#f4f8fe; }
/* 旧版表格固定列需隐藏主体副本，避免重复显示操作。 */
.records-table /deep/ .ant-table-fixed-columns-in-body { pointer-events:none; }
.records-table /deep/ .ant-table-fixed-columns-in-body > * { visibility:hidden; }
.cell-date { font-size:14px; line-height:22px; font-weight:500; white-space:nowrap; color:#253c55; font-variant-numeric:tabular-nums; }
.cell-meta { display:flex; align-items:center; flex-wrap:wrap; gap:8px; margin-top:5px; font-size:12px; line-height:18px; color:#687c91; }
.meta-separator { margin-left:8px; }
.cell-detail { max-width:218px; margin-top:4px; font-size:12px; line-height:18px; color:#687c91; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cell-pending { color:#8997a7; font-size:13px; }
.site-cell { max-width:148px; color:#253c55; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.point-tag { font-size:11px; padding:0 6px; border-radius:3px; background:#e7f5f6; color:#237a82; }
.point-tag.outlet { background:#eef1ff; color:#5262aa; }
.flow-tag { display:inline-flex; align-items:center; gap:6px; padding:3px 9px; border-radius:4px; font-size:12px; line-height:20px; background:#eef1f5; color:#586e85; }
.flow-tag.arrival { background:#fff3dc; color:#9b6514; }
.flow-tag.testing { background:#e8f2ff; color:#2867ba; }
.flow-tag.completed { background:#e5f5ed; color:#26805b; }
.flow-progress { display:flex; align-items:center; gap:4px; margin-top:7px; font-size:11px; color:#a5afbd; }
.flow-progress .done { color:#60788f; }
.flow-progress /deep/ .anticon { font-size:8px; color:#bac4cf; }
.row-actions { display:flex; align-items:center; gap:12px; white-space:nowrap; }
.action-link { border:0; padding:0; background:transparent; color:#2878cf; cursor:pointer; font-size:13px; line-height:24px; }
.action-link:hover { color:#1890ff; }
.action-link:disabled { opacity:.5; cursor:not-allowed; }
.row-actions /deep/ .ant-btn { font-size:12px; }
.delete-action { color:#d95353 !important; }
.expand-filter:focus-visible, .status-tab:focus-visible, .action-link:focus-visible { outline:2px solid #76baff; outline-offset:2px; }
@media (max-width:1400px) {
  .advanced-filters { grid-template-columns:1fr; gap:16px; }
  .advanced-fields { grid-template-columns:minmax(230px,276px) minmax(140px,180px); }
  .testing-filters .advanced-fields { grid-template-columns:minmax(230px,276px) minmax(130px,180px) minmax(150px,220px); }
}
@media (max-width:1100px) {
  .advanced-fields, .testing-filters .advanced-fields { grid-template-columns:1fr; }
}
@media (max-width:760px) {
  .filter-panel { padding:12px; }
  .quick-filters, .filter-actions { width:100%; }
  .date-filter { width:100%; }
  .person-filter, .point-filter { flex:1; width:auto; min-width:140px; }
  .advanced-fields, .testing-filters .advanced-fields { grid-template-columns:1fr; }
  .status-tab { padding:7px 9px; font-size:12px; }
  .list-toolbar { gap:8px; }
}
</style>
